// Privacy Policy content, per locale. LGPD-shaped. pt is the canonical
// text; en/es follow it. Subject to legal review.

import type { LegalContent } from "./types";

export const PRIVACY: LegalContent = {
  en: {
    metaTitle: "Privacy Policy — TrueHacking",
    metaDescription:
      "How TrueHacking collects, uses, and protects your data under the LGPD and applicable international privacy laws.",
    title: "Privacy Policy",
    effective: "Effective April 28, 2026",
    sections: [
      {
        title: "1. Who we are",
        blocks: [
          { p: 'This Privacy Policy explains how True Hacking ("we", "us", "our") collects, uses, shares, and protects information about you when you use the True Hacking platform at https://truehacking.ai (the "Service").' },
          { p: "True Hacking is the data controller responsible for your personal data under Lei Geral de Proteção de Dados Pessoais (LGPD, Lei 13.709/2018) and applicable international privacy laws." },
          { p: "For privacy-related inquiries, including exercising your data subject rights, contact our Data Protection Officer at support@truehacking.ai." },
        ],
      },
      {
        title: "2. Information we collect",
        blocks: [
          { p: "We collect information in three categories:" },
          { sub: "2.1 Account information you provide" },
          { ul: [
            "Email address (used as your login identifier)",
            "Password (stored only as a bcrypt hash; we never see the plaintext)",
            "Optional full name and organization name you supply at registration",
            "OAuth profile data (name, email, profile photo URL) if you sign in via Google or GitHub",
            "Two-factor authentication secrets if you enable 2FA",
          ] },
          { sub: "2.2 Information you submit when using the Service" },
          { ul: [
            "Scan targets (domains, IPs, URLs) you add to engagements",
            "Engagement metadata (names, descriptions, client labels)",
            "Manual review notes, severity adjustments, proof-of-concept text you write into findings",
            "Domain ownership verification proofs (DNS TXT records or HTTP files containing tokens we issue)",
          ] },
          { sub: "2.3 Information generated automatically" },
          { ul: [
            "Scan execution data: tool outputs, normalized evidence rows, correlated findings, AI enrichment results",
            "Audit logs: authentication events, administrative actions, data access (retained per the platform-level setting, default 365 days)",
            "Session metadata: IP address, user agent, login timestamps",
            "Refresh-token records (revoked sessions are kept for audit purposes)",
          ] },
        ],
      },
      {
        title: "3. Why we collect it (purposes and legal basis)",
        blocks: [
          { p: "Under LGPD, every category of processing requires a legal basis. We rely on the following:" },
          { ul: [
            { term: "Performance of contract", text: "(Art. 7, V) — to operate the Service: authenticate you, execute scans you request, store results, deliver reports." },
            { term: "Consent", text: "(Art. 7, I) — for OAuth sign-in via third-party providers and any future marketing communications. You can withdraw consent at any time." },
            { term: "Legitimate interest", text: "(Art. 7, IX) — to maintain security audit logs, detect abuse, enforce platform-level rate limits and quotas, debug production incidents." },
            { term: "Legal obligation", text: "(Art. 7, II) — to comply with tax, civil, and criminal record-keeping requirements when applicable." },
          ] },
        ],
      },
      {
        title: "4. Who we share it with",
        blocks: [
          { p: "We do not sell your data. We share it only with service providers strictly necessary to operate the platform:" },
          { ul: [
            { term: "Resend", text: "(transactional email provider, US) — to deliver password-reset and security notification emails. We send only your email address and the message body." },
            { term: "Hosting infrastructure", text: "— our application servers and database run on infrastructure provided by hosting partners who have signed appropriate data-processing agreements." },
            { term: "Future payment processor", text: "— when paid plans launch we will integrate a payment provider; this Policy will be updated to identify them and you will be notified before any billing data is collected." },
          ] },
          { p: "We will disclose data to law enforcement only when compelled by a valid court order or equivalent legal process, and we will notify you unless prohibited by law from doing so." },
        ],
      },
      {
        title: "5. International data transfers",
        blocks: [
          { p: "Some of our service providers (notably Resend) operate from outside Brazil. Where personal data is transferred internationally, we rely on the safeguards permitted under LGPD Art. 33, including standard contractual clauses and adequate-protection assessments of the receiving jurisdictions." },
        ],
      },
      {
        title: "6. How long we keep it",
        blocks: [
          { ul: [
            { term: "Account data", text: "— for as long as your account is active. Deleted within 30 days of account deletion, except where legal-obligation retention applies." },
            { term: "Engagement data", text: "(scan results, findings, evidence) — retained as long as the engagement exists. Deleted when you delete the engagement or your account." },
            { term: "Audit logs", text: "— retained for the period configured at the platform level (default 365 days), then automatically purged by a daily cron job." },
            { term: "Tool output files on disk", text: "— purged after 30 days by default to prevent unbounded disk consumption." },
            { term: "Revoked refresh tokens", text: "— kept indefinitely for audit purposes, anonymized of session metadata after 90 days." },
          ] },
        ],
      },
      {
        title: "7. Your rights under LGPD",
        blocks: [
          { p: "As the data subject, you have the following rights regarding your personal data (LGPD Art. 18):" },
          { ul: [
            "Confirmation of the existence of processing",
            "Access to your data",
            "Correction of incomplete, inaccurate, or outdated data",
            "Anonymization, blocking, or deletion of unnecessary or excessive data, or data processed in non-compliance with LGPD",
            "Data portability to another service provider",
            "Deletion of personal data processed with your consent (subject to the retention periods above)",
            "Information about public and private entities with which we have shared your data",
            "Information about the option to refuse consent and the consequences of refusal",
            "Revocation of consent",
          ] },
          { p: "Most of these you can exercise directly inside the Service:" },
          { ul: [
            "Access your data: visible throughout the dashboard and via the user profile",
            "Delete your account (and all associated personal data): Profile → Delete Account. This wipes credentials, memberships, and personal identifiers; engagement data is also removed unless otherwise specified.",
            "Export your data: contact us at support@truehacking.ai and we will provide a portable JSON export within 15 days.",
          ] },
          { p: "For any request you cannot complete in-app, contact our DPO at support@truehacking.ai. We respond to verified requests within 15 days as required by LGPD." },
        ],
      },
      {
        title: "8. How we protect your data",
        blocks: [
          { ul: [
            "All traffic is served over HTTPS with modern TLS configuration",
            "Passwords are stored only as bcrypt hashes (cost factor 12)",
            "Authentication uses short-lived JWT access tokens and rotation-on-use refresh tokens",
            "Two-factor authentication available and required for superuser accounts",
            "Server-side enforcement of all plan and quota limits (frontend disables are UX hints only)",
            "Per-tool subprocess memory limits prevent runaway tools from compromising other workloads",
            "HTTP rate limiting on authentication endpoints to mitigate credential stuffing",
            "Production-mode startup checks fail loud on insecure configuration (default secrets, wildcard CORS)",
            "Daily encrypted backups of the production database",
          ] },
          { p: "No system is impenetrable. If we discover a breach affecting your personal data, we will notify you and the Brazilian National Data Protection Authority (ANPD) within the timeframes required by LGPD." },
        ],
      },
      {
        title: "9. Cookies",
        blocks: [
          { p: "We use only functional cookies strictly necessary to operate the Service:" },
          { ul: [
            "Authentication cookies / localStorage entries holding your access and refresh tokens",
            "Session cookies for OAuth flows",
          ] },
          { p: "We do not use tracking, analytics, or advertising cookies. We do not currently integrate any third-party analytics service." },
        ],
      },
      {
        title: "10. Children's data",
        blocks: [
          { p: "The Service is not directed to children under 18. We do not knowingly collect personal data from minors. If you become aware that a child has provided us personal data, contact us and we will remove it." },
        ],
      },
      {
        title: "11. Changes to this Policy",
        blocks: [
          { p: "We may update this Policy to reflect changes in the Service, our practices, or legal requirements. The effective date at the top of this page reflects the most recent revision. For material changes we will notify registered users by email at least 15 days before the new version takes effect." },
        ],
      },
      {
        title: "12. Contact",
        blocks: [
          { p: "For any privacy-related questions, requests, or complaints, contact our Data Protection Officer at support@truehacking.ai." },
          { p: "You also have the right to lodge a complaint with the Autoridade Nacional de Proteção de Dados (ANPD) at gov.br/anpd." },
        ],
      },
    ],
  },

  pt: {
    metaTitle: "Política de Privacidade — TrueHacking",
    metaDescription:
      "Como a TrueHacking coleta, usa e protege seus dados sob a LGPD e leis de privacidade internacionais aplicáveis.",
    title: "Política de Privacidade",
    effective: "Vigente desde 28 de abril de 2026",
    sections: [
      {
        title: "1. Quem somos",
        blocks: [
          { p: 'Esta Política de Privacidade explica como a True Hacking ("nós", "nosso") coleta, usa, compartilha e protege informações sobre você quando você usa a plataforma True Hacking em https://truehacking.ai (o "Serviço").' },
          { p: "A True Hacking é a controladora dos dados responsável pelos seus dados pessoais sob a Lei Geral de Proteção de Dados Pessoais (LGPD, Lei 13.709/2018) e as leis de privacidade internacionais aplicáveis." },
          { p: "Para questões de privacidade, incluindo o exercício dos seus direitos de titular, contate nosso Encarregado de Proteção de Dados (DPO) em support@truehacking.ai." },
        ],
      },
      {
        title: "2. Informações que coletamos",
        blocks: [
          { p: "Coletamos informações em três categorias:" },
          { sub: "2.1 Informações de conta que você fornece" },
          { ul: [
            "Endereço de e-mail (usado como seu identificador de login)",
            "Senha (armazenada apenas como hash bcrypt; nunca vemos o texto puro)",
            "Nome completo e nome da organização opcionais que você fornece no cadastro",
            "Dados de perfil OAuth (nome, e-mail, URL da foto) se você entrar via Google ou GitHub",
            "Segredos de autenticação de dois fatores, se você ativar o 2FA",
          ] },
          { sub: "2.2 Informações que você envia ao usar o Serviço" },
          { ul: [
            "Alvos de scan (domínios, IPs, URLs) que você adiciona aos projetos",
            "Metadados do projeto (nomes, descrições, rótulos de cliente)",
            "Notas de revisão manual, ajustes de severidade e textos de prova de conceito que você escreve nos achados",
            "Comprovações de titularidade de domínio (registros DNS TXT ou arquivos HTTP contendo tokens que emitimos)",
          ] },
          { sub: "2.3 Informações geradas automaticamente" },
          { ul: [
            "Dados de execução de scan: saídas de ferramentas, evidências normalizadas, achados correlacionados, resultados de enriquecimento por IA",
            "Logs de auditoria: eventos de autenticação, ações administrativas, acessos a dados (retidos conforme a configuração da plataforma, padrão 365 dias)",
            "Metadados de sessão: endereço IP, user agent, horários de login",
            "Registros de refresh-token (sessões revogadas são mantidas para fins de auditoria)",
          ] },
        ],
      },
      {
        title: "3. Por que coletamos (finalidades e base legal)",
        blocks: [
          { p: "Sob a LGPD, cada categoria de tratamento exige uma base legal. Apoiamo-nos nas seguintes:" },
          { ul: [
            { term: "Execução de contrato", text: "(Art. 7, V) — para operar o Serviço: autenticar você, executar os scans que você solicita, armazenar resultados, entregar relatórios." },
            { term: "Consentimento", text: "(Art. 7, I) — para login OAuth via provedores terceiros e quaisquer comunicações de marketing futuras. Você pode retirar o consentimento a qualquer momento." },
            { term: "Legítimo interesse", text: "(Art. 7, IX) — para manter logs de auditoria de segurança, detectar abusos, aplicar limites de taxa e cotas da plataforma e depurar incidentes em produção." },
            { term: "Obrigação legal", text: "(Art. 7, II) — para cumprir requisitos de guarda de registros fiscais, civis e criminais quando aplicável." },
          ] },
        ],
      },
      {
        title: "4. Com quem compartilhamos",
        blocks: [
          { p: "Não vendemos seus dados. Compartilhamos apenas com prestadores de serviço estritamente necessários para operar a plataforma:" },
          { ul: [
            { term: "Resend", text: "(provedor de e-mail transacional, EUA) — para entregar e-mails de redefinição de senha e notificações de segurança. Enviamos apenas seu endereço de e-mail e o corpo da mensagem." },
            { term: "Infraestrutura de hospedagem", text: "— nossos servidores de aplicação e banco de dados rodam em infraestrutura de parceiros de hospedagem que assinaram os acordos de tratamento de dados adequados." },
            { term: "Futuro processador de pagamentos", text: "— quando os planos pagos forem lançados, integraremos um provedor de pagamento; esta Política será atualizada para identificá-lo e você será notificado antes de qualquer coleta de dados de cobrança." },
          ] },
          { p: "Divulgaremos dados às autoridades apenas quando obrigados por ordem judicial válida ou processo legal equivalente, e notificaremos você, salvo quando proibido por lei." },
        ],
      },
      {
        title: "5. Transferências internacionais de dados",
        blocks: [
          { p: "Alguns dos nossos prestadores (notadamente a Resend) operam fora do Brasil. Quando dados pessoais são transferidos internacionalmente, apoiamo-nos nas salvaguardas permitidas pelo Art. 33 da LGPD, incluindo cláusulas contratuais padrão e avaliações de proteção adequada das jurisdições receptoras." },
        ],
      },
      {
        title: "6. Por quanto tempo guardamos",
        blocks: [
          { ul: [
            { term: "Dados de conta", text: "— enquanto sua conta estiver ativa. Excluídos em até 30 dias após a exclusão da conta, exceto quando se aplica retenção por obrigação legal." },
            { term: "Dados de projeto", text: "(resultados de scan, achados, evidências) — retidos enquanto o projeto existir. Excluídos quando você apaga o projeto ou a conta." },
            { term: "Logs de auditoria", text: "— retidos pelo período configurado na plataforma (padrão 365 dias) e então automaticamente expurgados por uma rotina diária." },
            { term: "Arquivos de saída de ferramentas em disco", text: "— expurgados após 30 dias por padrão para evitar consumo ilimitado de disco." },
            { term: "Refresh-tokens revogados", text: "— mantidos indefinidamente para fins de auditoria, anonimizados dos metadados de sessão após 90 dias." },
          ] },
        ],
      },
      {
        title: "7. Seus direitos sob a LGPD",
        blocks: [
          { p: "Como titular dos dados, você tem os seguintes direitos sobre seus dados pessoais (LGPD Art. 18):" },
          { ul: [
            "Confirmação da existência de tratamento",
            "Acesso aos seus dados",
            "Correção de dados incompletos, inexatos ou desatualizados",
            "Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a LGPD",
            "Portabilidade dos dados a outro fornecedor de serviço",
            "Eliminação dos dados pessoais tratados com seu consentimento (sujeita aos prazos de retenção acima)",
            "Informação sobre as entidades públicas e privadas com as quais compartilhamos seus dados",
            "Informação sobre a possibilidade de não fornecer consentimento e as consequências da recusa",
            "Revogação do consentimento",
          ] },
          { p: "A maioria deles você pode exercer diretamente dentro do Serviço:" },
          { ul: [
            "Acessar seus dados: visíveis em todo o painel e no perfil do usuário",
            "Excluir sua conta (e todos os dados pessoais associados): Perfil → Excluir Conta. Isso apaga credenciais, vínculos e identificadores pessoais; os dados de projeto também são removidos, salvo indicação em contrário.",
            "Exportar seus dados: contate-nos em support@truehacking.ai e forneceremos uma exportação portável em JSON em até 15 dias.",
          ] },
          { p: "Para qualquer solicitação que você não consiga concluir no app, contate nosso DPO em support@truehacking.ai. Respondemos a solicitações verificadas em até 15 dias, conforme exigido pela LGPD." },
        ],
      },
      {
        title: "8. Como protegemos seus dados",
        blocks: [
          { ul: [
            "Todo o tráfego é servido sobre HTTPS com configuração TLS moderna",
            "As senhas são armazenadas apenas como hashes bcrypt (fator de custo 12)",
            "A autenticação usa tokens de acesso JWT de curta duração e refresh tokens com rotação no uso",
            "Autenticação de dois fatores disponível e obrigatória para contas de superusuário",
            "Aplicação server-side de todos os limites de plano e cota (desabilitações no frontend são apenas dicas de UX)",
            "Limites de memória por subprocesso de ferramenta evitam que ferramentas descontroladas comprometam outras cargas",
            "Limitação de taxa HTTP nos endpoints de autenticação para mitigar credential stuffing",
            "Verificações de inicialização em produção falham de forma ruidosa diante de configuração insegura (segredos padrão, CORS curinga)",
            "Backups criptografados diários do banco de dados de produção",
          ] },
          { p: "Nenhum sistema é impenetrável. Se descobrirmos uma violação que afete seus dados pessoais, notificaremos você e a Autoridade Nacional de Proteção de Dados (ANPD) nos prazos exigidos pela LGPD." },
        ],
      },
      {
        title: "9. Cookies",
        blocks: [
          { p: "Usamos apenas cookies funcionais estritamente necessários para operar o Serviço:" },
          { ul: [
            "Cookies de autenticação / entradas no localStorage com seus tokens de acesso e refresh",
            "Cookies de sessão para os fluxos OAuth",
          ] },
          { p: "Não usamos cookies de rastreamento, analytics ou publicidade. Atualmente não integramos nenhum serviço de analytics de terceiros." },
        ],
      },
      {
        title: "10. Dados de crianças",
        blocks: [
          { p: "O Serviço não é direcionado a menores de 18 anos. Não coletamos intencionalmente dados pessoais de menores. Se você souber que uma criança nos forneceu dados pessoais, contate-nos e os removeremos." },
        ],
      },
      {
        title: "11. Alterações nesta Política",
        blocks: [
          { p: "Podemos atualizar esta Política para refletir mudanças no Serviço, em nossas práticas ou em requisitos legais. A data de vigência no topo desta página reflete a revisão mais recente. Para mudanças relevantes, notificaremos os usuários registrados por e-mail com pelo menos 15 dias de antecedência da entrada em vigor da nova versão." },
        ],
      },
      {
        title: "12. Contato",
        blocks: [
          { p: "Para quaisquer dúvidas, solicitações ou reclamações sobre privacidade, contate nosso Encarregado de Proteção de Dados em support@truehacking.ai." },
          { p: "Você também tem o direito de apresentar uma reclamação à Autoridade Nacional de Proteção de Dados (ANPD) em gov.br/anpd." },
        ],
      },
    ],
  },

  es: {
    metaTitle: "Política de Privacidad — TrueHacking",
    metaDescription:
      "Cómo TrueHacking recopila, usa y protege tus datos bajo la LGPD y las leyes de privacidad internacionales aplicables.",
    title: "Política de Privacidad",
    effective: "Vigente desde el 28 de abril de 2026",
    sections: [
      {
        title: "1. Quiénes somos",
        blocks: [
          { p: 'Esta Política de Privacidad explica cómo True Hacking ("nosotros", "nuestro") recopila, usa, comparte y protege información sobre ti cuando usas la plataforma True Hacking en https://truehacking.ai (el "Servicio").' },
          { p: "True Hacking es el controlador de datos responsable de tus datos personales bajo la Lei Geral de Proteção de Dados Pessoais (LGPD, Lei 13.709/2018) y las leyes de privacidad internacionales aplicables." },
          { p: "Para consultas de privacidad, incluido el ejercicio de tus derechos como titular, contacta a nuestro Encargado de Protección de Datos (DPO) en support@truehacking.ai." },
        ],
      },
      {
        title: "2. Información que recopilamos",
        blocks: [
          { p: "Recopilamos información en tres categorías:" },
          { sub: "2.1 Información de cuenta que proporcionas" },
          { ul: [
            "Dirección de correo (usada como tu identificador de inicio de sesión)",
            "Contraseña (almacenada solo como hash bcrypt; nunca vemos el texto plano)",
            "Nombre completo y nombre de organización opcionales que indicas al registrarte",
            "Datos de perfil OAuth (nombre, correo, URL de foto) si inicias sesión vía Google o GitHub",
            "Secretos de autenticación de dos factores si activas el 2FA",
          ] },
          { sub: "2.2 Información que envías al usar el Servicio" },
          { ul: [
            "Objetivos de scan (dominios, IPs, URLs) que agregas a los proyectos",
            "Metadatos del proyecto (nombres, descripciones, etiquetas de cliente)",
            "Notas de revisión manual, ajustes de severidad y textos de prueba de concepto que escribes en los hallazgos",
            "Comprobaciones de titularidad de dominio (registros DNS TXT o archivos HTTP con tokens que emitimos)",
          ] },
          { sub: "2.3 Información generada automáticamente" },
          { ul: [
            "Datos de ejecución de scan: salidas de herramientas, evidencias normalizadas, hallazgos correlacionados, resultados de enriquecimiento por IA",
            "Registros de auditoría: eventos de autenticación, acciones administrativas, accesos a datos (retenidos según la configuración de la plataforma, por defecto 365 días)",
            "Metadatos de sesión: dirección IP, user agent, marcas de tiempo de inicio de sesión",
            "Registros de refresh-token (las sesiones revocadas se conservan con fines de auditoría)",
          ] },
        ],
      },
      {
        title: "3. Por qué la recopilamos (finalidades y base legal)",
        blocks: [
          { p: "Bajo la LGPD, cada categoría de tratamiento requiere una base legal. Nos apoyamos en las siguientes:" },
          { ul: [
            { term: "Ejecución de contrato", text: "(Art. 7, V) — para operar el Servicio: autenticarte, ejecutar los scans que solicitas, almacenar resultados, entregar informes." },
            { term: "Consentimiento", text: "(Art. 7, I) — para el inicio de sesión OAuth vía proveedores externos y cualquier comunicación de marketing futura. Puedes retirar el consentimiento en cualquier momento." },
            { term: "Interés legítimo", text: "(Art. 7, IX) — para mantener registros de auditoría de seguridad, detectar abusos, aplicar límites de tasa y cuotas de la plataforma y depurar incidentes en producción." },
            { term: "Obligación legal", text: "(Art. 7, II) — para cumplir requisitos de conservación de registros fiscales, civiles y penales cuando corresponda." },
          ] },
        ],
      },
      {
        title: "4. Con quién la compartimos",
        blocks: [
          { p: "No vendemos tus datos. Los compartimos solo con proveedores de servicio estrictamente necesarios para operar la plataforma:" },
          { ul: [
            { term: "Resend", text: "(proveedor de correo transaccional, EE. UU.) — para entregar correos de restablecimiento de contraseña y notificaciones de seguridad. Enviamos solo tu dirección de correo y el cuerpo del mensaje." },
            { term: "Infraestructura de hosting", text: "— nuestros servidores de aplicación y base de datos corren en infraestructura de socios de hosting que han firmado los acuerdos de tratamiento de datos adecuados." },
            { term: "Futuro procesador de pagos", text: "— cuando se lancen los planes de pago integraremos un proveedor de pagos; esta Política se actualizará para identificarlo y se te notificará antes de recopilar cualquier dato de facturación." },
          ] },
          { p: "Divulgaremos datos a las autoridades solo cuando seamos obligados por una orden judicial válida o proceso legal equivalente, y te notificaremos salvo que la ley lo prohíba." },
        ],
      },
      {
        title: "5. Transferencias internacionales de datos",
        blocks: [
          { p: "Algunos de nuestros proveedores (en particular Resend) operan fuera de Brasil. Cuando los datos personales se transfieren internacionalmente, nos apoyamos en las salvaguardas permitidas por el Art. 33 de la LGPD, incluidas cláusulas contractuales estándar y evaluaciones de protección adecuada de las jurisdicciones receptoras." },
        ],
      },
      {
        title: "6. Cuánto tiempo los conservamos",
        blocks: [
          { ul: [
            { term: "Datos de cuenta", text: "— mientras tu cuenta esté activa. Eliminados dentro de los 30 días tras la eliminación de la cuenta, salvo cuando aplique retención por obligación legal." },
            { term: "Datos de proyecto", text: "(resultados de scan, hallazgos, evidencias) — conservados mientras exista el proyecto. Eliminados cuando borras el proyecto o tu cuenta." },
            { term: "Registros de auditoría", text: "— conservados por el período configurado en la plataforma (por defecto 365 días) y luego purgados automáticamente por una tarea diaria." },
            { term: "Archivos de salida de herramientas en disco", text: "— purgados tras 30 días por defecto para evitar un consumo de disco ilimitado." },
            { term: "Refresh-tokens revocados", text: "— conservados indefinidamente con fines de auditoría, anonimizados de los metadatos de sesión tras 90 días." },
          ] },
        ],
      },
      {
        title: "7. Tus derechos bajo la LGPD",
        blocks: [
          { p: "Como titular de los datos, tienes los siguientes derechos sobre tus datos personales (LGPD Art. 18):" },
          { ul: [
            "Confirmación de la existencia de tratamiento",
            "Acceso a tus datos",
            "Corrección de datos incompletos, inexactos o desactualizados",
            "Anonimización, bloqueo o eliminación de datos innecesarios, excesivos o tratados en incumplimiento de la LGPD",
            "Portabilidad de los datos a otro proveedor de servicio",
            "Eliminación de los datos personales tratados con tu consentimiento (sujeta a los plazos de retención anteriores)",
            "Información sobre las entidades públicas y privadas con las que compartimos tus datos",
            "Información sobre la posibilidad de no dar consentimiento y las consecuencias de la negativa",
            "Revocación del consentimiento",
          ] },
          { p: "La mayoría de ellos los puedes ejercer directamente dentro del Servicio:" },
          { ul: [
            "Acceder a tus datos: visibles en todo el panel y en el perfil de usuario",
            "Eliminar tu cuenta (y todos los datos personales asociados): Perfil → Eliminar Cuenta. Esto borra credenciales, membresías e identificadores personales; los datos de proyecto también se eliminan, salvo indicación en contrario.",
            "Exportar tus datos: contáctanos en support@truehacking.ai y proporcionaremos una exportación portable en JSON dentro de 15 días.",
          ] },
          { p: "Para cualquier solicitud que no puedas completar en la app, contacta a nuestro DPO en support@truehacking.ai. Respondemos a solicitudes verificadas dentro de 15 días, según lo exige la LGPD." },
        ],
      },
      {
        title: "8. Cómo protegemos tus datos",
        blocks: [
          { ul: [
            "Todo el tráfico se sirve sobre HTTPS con configuración TLS moderna",
            "Las contraseñas se almacenan solo como hashes bcrypt (factor de costo 12)",
            "La autenticación usa tokens de acceso JWT de corta duración y refresh tokens con rotación en el uso",
            "Autenticación de dos factores disponible y obligatoria para cuentas de superusuario",
            "Aplicación del lado del servidor de todos los límites de plan y cuota (las desactivaciones en el frontend son solo indicaciones de UX)",
            "Límites de memoria por subproceso de herramienta evitan que herramientas descontroladas comprometan otras cargas",
            "Limitación de tasa HTTP en los endpoints de autenticación para mitigar el credential stuffing",
            "Las verificaciones de arranque en producción fallan de forma ruidosa ante configuración insegura (secretos por defecto, CORS comodín)",
            "Copias de seguridad cifradas diarias de la base de datos de producción",
          ] },
          { p: "Ningún sistema es impenetrable. Si descubrimos una brecha que afecte tus datos personales, te notificaremos a ti y a la Autoridad Nacional de Protección de Datos de Brasil (ANPD) dentro de los plazos exigidos por la LGPD." },
        ],
      },
      {
        title: "9. Cookies",
        blocks: [
          { p: "Usamos solo cookies funcionales estrictamente necesarias para operar el Servicio:" },
          { ul: [
            "Cookies de autenticación / entradas en localStorage con tus tokens de acceso y refresh",
            "Cookies de sesión para los flujos OAuth",
          ] },
          { p: "No usamos cookies de rastreo, analítica o publicidad. Actualmente no integramos ningún servicio de analítica de terceros." },
        ],
      },
      {
        title: "10. Datos de menores",
        blocks: [
          { p: "El Servicio no está dirigido a menores de 18 años. No recopilamos intencionalmente datos personales de menores. Si te enteras de que un menor nos ha proporcionado datos personales, contáctanos y los eliminaremos." },
        ],
      },
      {
        title: "11. Cambios en esta Política",
        blocks: [
          { p: "Podemos actualizar esta Política para reflejar cambios en el Servicio, en nuestras prácticas o en requisitos legales. La fecha de vigencia en la parte superior de esta página refleja la revisión más reciente. Para cambios relevantes, notificaremos a los usuarios registrados por correo al menos 15 días antes de que la nueva versión entre en vigor." },
        ],
      },
      {
        title: "12. Contacto",
        blocks: [
          { p: "Para cualquier duda, solicitud o reclamo sobre privacidad, contacta a nuestro Encargado de Protección de Datos en support@truehacking.ai." },
          { p: "También tienes derecho a presentar un reclamo ante la Autoridad Nacional de Protección de Datos (ANPD) en gov.br/anpd." },
        ],
      },
    ],
  },
};
